import {
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  IconButton,
  Collapse,
  Badge
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Lock, LockOpen, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../resources/functions';
import TableCell from '../TableCell';
import Title from '../Title';
import { ScootersLockHistoryTableProps } from './ScootersLockHistoryTableTypes';
import {
  getLockHistoryStatus,
  getLockHistoryStatusDisplay
} from './lockHistoryStatus';
import MobileCardList, { MobileCardItem } from '../MobileCardList';
import useIsMobile from '../../hooks/useIsMobile';

const ScootersLockHistoryTable = ({
  isLocked,
  rows,
  isScooterId
}: ScootersLockHistoryTableProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  // Групуємо послідовні рядки з однаковим самокатом та дією
  const groupedRows = rows.reduce((acc: any[], row, index) => {
    const currentScooterId = row.scooterId?._id;
    const currentStatus = getLockHistoryStatus(row);
    
    if (index === 0) {
      acc.push({
        isGroup: false,
        rows: [row],
        scooterId: currentScooterId,
        status: currentStatus
      });
    } else {
      const lastGroup = acc[acc.length - 1];
      const prevScooterId = lastGroup.scooterId;
      const prevStatus = lastGroup.status;
      
      // Якщо самокат і статус такі самі - додаємо до групи
      if (currentScooterId === prevScooterId && currentStatus === prevStatus) {
        lastGroup.rows.push(row);
        lastGroup.isGroup = lastGroup.rows.length > 1;
      } else {
        // Інакше створюємо нову групу
        acc.push({
          isGroup: false,
          rows: [row],
          scooterId: currentScooterId,
          status: currentStatus
        });
      }
    }
    
    return acc;
  }, []);

  const toggleGroup = (groupIndex: number) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupIndex)) {
        newSet.delete(groupIndex);
      } else {
        newSet.add(groupIndex);
      }
      return newSet;
    });
  };

  const mobileItems: MobileCardItem[] = groupedRows.flatMap(
    (group: any, groupIndex: number) => {
      const isExpanded = expandedGroups.has(groupIndex);
      const displayRows =
        group.isGroup && !isExpanded ? [group.rows[0]] : group.rows;

      return displayRows.map((row: any, rowIndex: number) => {
        const statusDisplay = getLockHistoryStatusDisplay(
          getLockHistoryStatus(row),
          t
        );

        return {
          id: row._id,
          title: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isLocked ? (
                <Lock
                  sx={{
                    fontSize: 16,
                    color: theme.palette.mode === 'dark' ? '#5073F8' : '#1976D2'
                  }}
                />
              ) : (
                <LockOpen
                  sx={{
                    fontSize: 16,
                    color: theme.palette.mode === 'dark' ? '#5073F8' : '#4CAF50'
                  }}
                />
              )}
              <span>{row.scooterId?.name}</span>
              {group.isGroup && rowIndex === 0 && (
                <IconButton
                  onClick={() => toggleGroup(groupIndex)}
                  size='small'
                  sx={{ ml: 0.5, p: 0.5 }}
                >
                  <Badge
                    badgeContent={group.rows.length}
                    color='primary'
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#FFE082',
                        color: '#000000'
                      }
                    }}
                  >
                    {isExpanded ? (
                      <ExpandLess sx={{ fontSize: 20 }} />
                    ) : (
                      <ExpandMore sx={{ fontSize: 20 }} />
                    )}
                  </Badge>
                </IconButton>
              )}
            </Box>
          ),
          fields: [
            {
              label: t('lockHistory.colTime'),
              value: formatDate(row.createdAt, { time: true })
            },
            ...(isScooterId
              ? []
              : [
                  {
                    label: t('lockHistory.colUser'),
                    value: row?.adminId ? row?.adminId?.login ?? row.type : '-'
                  }
                ]),
            {
              label: t('lockHistory.colStatus'),
              value: (
                <Chip
                  label={statusDisplay.label}
                  size='small'
                  sx={{
                    bgcolor: statusDisplay.bgcolor,
                    color: statusDisplay.color,
                    fontWeight: 500
                  }}
                />
              ),
              wide: true
            }
          ]
        };
      });
    }
  );

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isLocked ? (
            <Lock sx={{ color: theme.palette.mode === 'dark' ? '#5073F8' : '#424242' }} />
          ) : (
            <LockOpen sx={{ color: theme.palette.mode === 'dark' ? '#5073F8' : '#424242' }} />
          )}
          <Title variant='h5'>{isLocked ? t('lockHistory.locked') : t('lockHistory.unlocked')}</Title>
        </Box>
      </Grid>
      <Grid item sx={{ width: '100%' }}>
        {isMobile ? (
          <MobileCardList
            emptyText={t('lockHistory.emptyState')}
            items={mobileItems}
          />
        ) : (
        <TableContainer sx={{ width: '100%' }}>
          <Table sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5',
                    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#424242',
                    padding: '10px 16px',
                    fontWeight: 600,
                    borderBottom: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : '1px solid #424242',
                    width: '20%'
                  }}
                >
                  {t('lockHistory.colScooter')}
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5',
                    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#424242',
                    padding: '10px 16px',
                    fontWeight: 600,
                    borderBottom: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : '1px solid #424242',
                    width: '25%'
                  }}
                >
                  {t('lockHistory.colTime')}
                </TableCell>
                <TableCell 
                  sx={{ 
                    backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5',
                    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#424242',
                    padding: '10px 16px',
                    fontWeight: 600,
                    borderBottom: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : '1px solid #424242',
                    width: isScooterId ? '55%' : '30%'
                  }}
                >
                  {t('lockHistory.colStatus')}
                </TableCell>
                {!isScooterId && (
                  <TableCell 
                    sx={{ 
                      backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#F5F5F5',
                      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#424242',
                      padding: '10px 16px',
                      fontWeight: 600,
                      borderBottom: theme.palette.mode === 'light' ? '1px solid #E0E0E0' : '1px solid #424242',
                      width: '25%'
                    }}
                  >
                    {t('lockHistory.colUser')}
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedRows.map((group, groupIndex) => {
                const isExpanded = expandedGroups.has(groupIndex);
                const firstRow = group.rows[0];
                const displayRows = group.isGroup && !isExpanded ? [firstRow] : group.rows;
                
                return displayRows.map((row: any, rowIndex: number) => {
                  const isFirstInGroup = rowIndex === 0;
                  const status = getLockHistoryStatus(row);
                  const statusDisplay = getLockHistoryStatusDisplay(status, t);
                  
                  return (
                    <TableRow key={row._id}>
                      <TableCell sx={{ padding: '10px 16px', border: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isLocked ? (
                            <Lock 
                              sx={{ 
                                fontSize: 16,
                                color: theme.palette.mode === 'dark' ? '#5073F8' : '#1976D2' 
                              }} 
                            />
                          ) : (
                            <LockOpen 
                              sx={{ 
                                fontSize: 16,
                                color: theme.palette.mode === 'dark' ? '#5073F8' : '#4CAF50' 
                              }} 
                            />
                          )}
                          <span>{row.scooterId?.name}</span>
                          {group.isGroup && isFirstInGroup && (
                            <IconButton
                              size="small"
                              onClick={() => toggleGroup(groupIndex)}
                              sx={{ ml: 1, p: 0.5 }}
                            >
                              <Badge 
                                badgeContent={group.rows.length} 
                                color="primary"
                                sx={{
                                  '& .MuiBadge-badge': {
                                    backgroundColor: '#FFE082',
                                    color: '#000000'
                                  }
                                }}
                              >
                                {isExpanded ? (
                                  <ExpandLess sx={{ fontSize: 20 }} />
                                ) : (
                                  <ExpandMore sx={{ fontSize: 20 }} />
                                )}
                              </Badge>
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ padding: '10px 16px', border: 'none' }}>
                        {formatDate(row.createdAt, { time: true })}
                      </TableCell>
                      <TableCell sx={{ padding: '10px 16px', border: 'none' }}>
                        <Chip
                          label={statusDisplay.label}
                          size='small'
                          sx={{
                            bgcolor: statusDisplay.bgcolor,
                            color: statusDisplay.color,
                            fontWeight: 500,
                            minWidth: 150
                          }}
                        />
                      </TableCell>
                      {!isScooterId && (
                        <TableCell sx={{ padding: '10px 16px', border: 'none' }}>
                          {row?.adminId ? row?.adminId?.login ?? row.type : ''}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
        </TableContainer>
        )}
      </Grid>
    </Grid>
  );
};

export default ScootersLockHistoryTable;
